import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { Chip, Typography } from '@mui/material';

import { ComponentModel } from '../../../api/api';
import SidePanelList, { SidePanelListColumn } from '../../../components/Panels/SidePanelList';

interface ComponentsPanelProps {
	onClose: () => void;
	onComponentSelect: (componentId: string) => void;
	selectedComponentId?: string | null;
	components?: ComponentModel[];
	isLoading?: boolean;
	isFetching?: boolean;
	error?: Error | null;
	onRefresh?: () => void;
	onCollapseChange?: (collapsed: boolean) => void;
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	top?: number;
}

export const COMPONENTS_PANEL_STORAGE_KEY = 'componentsPanel.width';
export const COMPONENTS_PANEL_DEFAULT_WIDTH = 500;
export const COMPONENTS_PANEL_MIN_WIDTH = 360;
export const COMPONENTS_PANEL_MAX_WIDTH = 900;
export const COMPONENTS_PANEL_COLLAPSED_WIDTH = 48;

export default function ComponentsPanel({
	onClose,
	onComponentSelect,
	selectedComponentId,
	components = [],
	isLoading = false,
	isFetching = false,
	error = null,
	onRefresh,
	onCollapseChange,
	width,
	setWidth,
	top = 0,
}: ComponentsPanelProps) {
	const [isCollapsed, setIsCollapsed] = useState(false);

	const handleToggleCollapse = () => {
		const newCollapsed = !isCollapsed;
		setIsCollapsed(newCollapsed);
		if (onCollapseChange) {
			onCollapseChange(newCollapsed);
		}
	};

	const handleRefresh = () => {
		if (onRefresh) {
			onRefresh();
		}
	};

	// Get components array
	const componentsList = useMemo(() => components || [], [components]);

	// Define table columns
	const columns: SidePanelListColumn[] = [
		{ id: 'name', label: 'Name', align: 'left' },
		{ id: 'containerImage', label: 'Container Image', align: 'left' },
		{ id: 'dependencies', label: 'Dependencies', align: 'center' },
	];

	// Render cell content based on column
	const renderCell = (component: ComponentModel, columnId: string) => {
		switch (columnId) {
			case 'name':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
						{component.name}
					</Typography>
				);
			case 'containerImage':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
						{component.containerImageName}:{component.containerImageVersion}
					</Typography>
				);
			case 'dependencies':
				return <Chip label={component.dependencies?.length || 0} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
			default:
				return null;
		}
	};

	return (
		<SidePanelList
			title="Components"
			subtitle="All available components"
			items={componentsList}
			columns={columns}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error as Error | null}
			emptyMessage="No components found"
			selectedItemId={selectedComponentId}
			width={width}
			collapsedWidth={COMPONENTS_PANEL_COLLAPSED_WIDTH}
			isCollapsed={isCollapsed}
			onClose={onClose}
			onRefresh={handleRefresh}
			onItemSelect={(component) => onComponentSelect(component.id)}
			renderCell={renderCell}
			getItemId={(component) => component.id}
			onToggleCollapse={handleToggleCollapse}
			resizeStorageKey={COMPONENTS_PANEL_STORAGE_KEY}
			defaultWidth={COMPONENTS_PANEL_DEFAULT_WIDTH}
			minWidth={COMPONENTS_PANEL_MIN_WIDTH}
			maxWidth={COMPONENTS_PANEL_MAX_WIDTH}
			setWidth={setWidth}
			top={top}
		/>
	);
}
